import {
	CanvasEntityState,
	ChangeSet,
	ChangeSetEntityChangeType,
	DRAGGABLE_ELEMENT_TYPES,
	EntityWithLogicScopeState,
	ENTRY_POINT_TYPES,
	EntryPointEntityState,
	filterOutDuplicateEntitiesAsync,
	getParentCanvasEntity,
	getScopeOwner,
	ProjectState,
	resolveEntityName,
} from '@elyx-code/project-logic-tree';
import { Area } from './area';
import { YieldOptions, YieldTracker } from '@elyx-code/common-ts-utils';

export async function toCanvasFlatArray(
	project: ProjectState,
	changeSet: ChangeSet,
	options: YieldOptions & { forceAll?: boolean } = {},
): Promise<CanvasEntityState[]> {
	const tracker = YieldTracker.from(options);

	const prePositionedEntities: CanvasEntityState[] = [];
	const entitiesWithoutCoordinates: CanvasEntityState[] = [];

	for (const instance of project.instancesList) {
		// Some entities are not draggable/canvas entities, and some others that usually are, sometimes are not
		// (e.g. A function-declaration or definition-entity can be nested inside other entities)
		if (
			!DRAGGABLE_ELEMENT_TYPES.includes(instance.type) ||
			getParentCanvasEntity(instance)?.id !== instance.id
		) {
			continue;
		}

		// Check if the entity should be rendered, based on whether their parent scope entity is collapsed (hiding its body)

		// Recursively, while the parent of the scope owner isn't the project state
		// we should keep checking if the parent is collapsed
		// This is because the current entity could be nested inside of two entry-point entities
		// The inmediate parent, for example the body of the loop, could be un-collapsed, while the entire root scope owner is collapsed
		let topScopeOwner = getScopeOwner(instance, project) as
			| EntityWithLogicScopeState
			| ProjectState;

		if (!topScopeOwner) {
			continue;
		}

		if (
			ENTRY_POINT_TYPES.includes(topScopeOwner.type) &&
			(topScopeOwner as EntryPointEntityState).collapsed &&
			(topScopeOwner as EntryPointEntityState).id !== instance.id
		) {
			continue;
		}

		while (
			!!(topScopeOwner as EntityWithLogicScopeState).parent &&
			(topScopeOwner as EntityWithLogicScopeState).parent !== project
		) {
			topScopeOwner = (topScopeOwner as EntityWithLogicScopeState)
				.parent as EntityWithLogicScopeState;

			if (
				ENTRY_POINT_TYPES.includes(topScopeOwner.type) &&
				(topScopeOwner as EntryPointEntityState).collapsed &&
				(topScopeOwner as EntryPointEntityState).id !== instance.id
			) {
				continue;
			}
		}

		if (options.forceAll) {
			// @ts-ignore
			instance.x = 0;
			// @ts-ignore
			instance.y = 0;
			entitiesWithoutCoordinates.push(instance as CanvasEntityState);
		} else {
			if (
				!(instance as CanvasEntityState).x ||
				!(instance as CanvasEntityState).y
			) {
				entitiesWithoutCoordinates.push(instance as CanvasEntityState);
			} else {
				prePositionedEntities.push(instance as CanvasEntityState);
			}
		}

		await tracker.tick();
	}

	const results = (await filterOutDuplicateEntitiesAsync([
		...prePositionedEntities,
		...Area.autoGenerateLayout(project, changeSet, entitiesWithoutCoordinates),
	])) as CanvasEntityState[];

	return results;
}
